import type { Transaction } from '@/types';
import type { SpaceBatchPreview, SpaceDetail, SpaceExpense, SpaceMember, SpaceSendRule, SpaceSendRuleType, SendToSpaceRequest, SendToSpaceResult } from '@/types/spaces';
import { spaceHref, spaceHrefForPeriod } from './navigation';
import { settlementPeriodLabel } from './settlement';

export function transactionCents(tx: Transaction): number | null {
  const money = tx.money;
  if (money && (!money.reporting || money.reportingStatus === 'unavailable')) return null;
  if (!money && tx.currencyCode && tx.currencyCode.toUpperCase() !== 'BRL') return null;
  const amount = money?.reporting?.amount ?? tx.amount;
  const cents = Math.round((Math.abs(amount) + Number.EPSILON) * 100);
  return Number.isSafeInteger(cents) && cents > 0 ? cents : null;
}

export function canSendTransaction(tx: Transaction): boolean {
  return tx.isExpense && !tx.isInternalTransfer && !tx.isCashFlowNeutral && transactionCents(tx) !== null;
}

export function partialCents(value: string): number | null {
  const trimmed = value.trim();
  if (!/^\d{1,12}(?:[,.]\d{1,2})?$/.test(trimmed)) return null;
  const [whole, fraction = ''] = trimmed.replace(',', '.').split('.');
  const cents = Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
  return Number.isSafeInteger(cents) && cents > 0 ? cents : null;
}

export function partialAmountError(value: string, total: number | null): string | null {
  const amount = partialCents(value);
  if (amount === null) return 'Informe um valor maior que zero, com até duas casas decimais (ex.: 25,50).';
  if (total === null || amount > total) return 'A parte enviada não pode ultrapassar o valor do lançamento.';
  return null;
}

// What this draft would put in the space, or null while it cannot be sent: a partial amount that is missing, not a
// positive value, or above the purchase. The split, the member preview and the buttons read this one value, so an
// invalid amount is never divided among the members or previewed as if it were real.
export function sendableCents(draft: Pick<SendDraft, 'mode' | 'amount'>, tx: Transaction): number | null {
  const total = transactionCents(tx);
  if (total === null) return null;
  if (draft.mode !== 'partial') return total;
  const amount = partialCents(draft.amount);
  return amount !== null && amount <= total ? amount : null;
}

// A "part" equal to the whole purchase is the whole purchase: nothing stays unsent. The request, the remainder line,
// the member preview and the telemetry event all read this one rule, so they cannot disagree at the boundary.
export function sendsPart(draft: Pick<SendDraft, 'mode' | 'amount'>, tx: Transaction): boolean {
  const total = transactionCents(tx);
  const amount = partialCents(draft.amount);
  return draft.mode === 'partial' && amount !== null && total !== null && amount < total;
}

export function sendRuleChoices(transaction: Transaction): { value: string; type: SpaceSendRuleType; tagId: string; label: string }[] {
  const choices: ReturnType<typeof sendRuleChoices> = [];
  if (transaction.description.trim()) choices.push({ value: 'description', type: 'description', tagId: '', label: `a descrição for “${transaction.description}”` });
  if ((transaction.customCategoryId ?? transaction.categoryId)?.trim()) choices.push({ value: 'category', type: 'category', tagId: '', label: `a categoria for ${transaction.categoryTranslated ?? transaction.category ?? 'a deste lançamento'}` });
  for (const tag of transaction.tags ?? []) {
    if (tag.id.trim()) choices.push({ value: `tag:${tag.id}`, type: 'tag', tagId: tag.id, label: `tiver a tag #${tag.name}` });
  }
  return choices;
}

export function selectedSendRule(transaction: Transaction, draft: Pick<SendDraft, 'ruleType' | 'tagId'>): ReturnType<typeof sendRuleChoices>[number] | undefined {
  const choices = sendRuleChoices(transaction);
  return choices.find((choice) => choice.type === draft.ruleType && choice.tagId === draft.tagId) ?? choices[0];
}

export function splitSpaceAmount(cents: number, members: SpaceMember[]): { member: SpaceMember; cents: number }[] {
  const weights = members.map((member) => Math.round(member.effectivePct * 100));
  const total = weights.reduce((sum, value) => sum + value, 0);
  if (!total || !Number.isSafeInteger(cents) || cents < 0) return [];
  const denominator = BigInt(total);
  const parts = weights.map((weight, index) => {
    const numerator = BigInt(cents) * BigInt(weight);
    return { index, cents: Number(numerator / denominator), remainder: numerator % denominator };
  });
  let remaining = cents - parts.reduce((sum, part) => sum + part.cents, 0);
  const ranked = [...parts].sort((a, b) => Number(b.remainder - a.remainder) || b.index - a.index);
  for (const part of ranked) { if (remaining-- > 0) part.cents++; }
  return parts.map((part) => ({ member: members[part.index], cents: part.cents }));
}

// The API's sendable count includes the selected row, even when it is being adjusted.
export function additionalBatchCount(preview?: SpaceBatchPreview): number {
  return preview && !preview.exceedsLimit ? Math.max(0, preview.sendableCount - 1) : 0;
}

export function matchingSendRules(tx: Transaction, rules: SpaceSendRule[], spaceId: string): SpaceSendRule[] {
  const occurred = Date.parse(tx.date);
  return rules.filter((rule) => {
    if (rule.spaceId !== spaceId || !rule.enabled) return false;
    if (rule.windowStart && occurred < Date.parse(rule.windowStart)) return false;
    if (rule.windowEnd && occurred > Date.parse(rule.windowEnd)) return false;
    switch (rule.type) {
      case 'description': return rule.value === tx.description.trim().toLowerCase();
      case 'category': return rule.value === (tx.customCategoryId ?? tx.categoryId);
      case 'tag': return Boolean(tx.tags?.some((tag) => tag.id === rule.value));
      case 'recurring': return rule.value === tx.recurringPaymentId;
      default: return false; // Merchant identity is server-owned and absent from transaction DTOs.
    }
  });
}

export type SendMode = 'full' | 'partial' | 'recurring';
export interface SendDraft {
  spaceId: string; mode: SendMode; amount: string; note: string; batch: boolean;
  makeRule: boolean; ruleType: SpaceSendRuleType; tagId: string; preview: boolean; previewMemberId: string; error: string | null;
}
type DraftField = { [K in keyof SendDraft]: { type: 'field'; field: K; value: SendDraft[K] } }[keyof SendDraft];
export type SendAction = DraftField | { type: 'space'; spaceId: string; expense?: SpaceExpense };

export function initialSendDraft(spaceId: string, expense?: SpaceExpense): SendDraft {
  return { spaceId, mode: expense?.partial ? 'partial' : 'full',
    amount: expense ? (expense.sharedAmountCents / 100).toFixed(2).replace('.', ',') : '',
    note: expense?.note ?? '', batch: false, makeRule: false, ruleType: 'description', tagId: '', preview: false, previewMemberId: '', error: null };
}

export function sendDraftReducer(state: SendDraft, action: SendAction): SendDraft {
  if (action.type === 'space') return initialSendDraft(action.spaceId, action.expense);
  return { ...state, [action.field]: action.value, error: action.field === 'error' ? action.value as string | null : null };
}

export function sendRequest(tx: Transaction, space: SpaceDetail, draft: SendDraft, batch?: SpaceBatchPreview): SendToSpaceRequest {
  if (!canSendTransaction(tx) || space.myRole !== 'editor' || space.archivedAt) throw new Error('Este lançamento não pode ser enviado para esse espaço.');
  const amount = draft.mode === 'partial' ? partialCents(draft.amount) : transactionCents(tx);
  if (amount === null || amount > transactionCents(tx)!) throw new Error('Informe uma parte maior que zero e até o valor do lançamento.');
  if (draft.batch && !additionalBatchCount(batch)) throw new Error('Revise os lançamentos com a mesma descrição antes de enviar.');
  if (draft.mode === 'recurring' && !tx.recurringPaymentId) throw new Error('Este lançamento ainda não está ligado a uma recorrência.');
  const selectedRule = selectedSendRule(tx, draft);
  if (draft.makeRule && draft.mode !== 'recurring' && !selectedRule) throw new Error('Este lançamento não tem descrição, categoria ou tag para criar uma regra. Desmarque o envio automático.');
  const alwaysSendBy = draft.mode === 'recurring' ? 'recurring' : draft.makeRule ? selectedRule!.type : null;
  if (draft.note.trim().length > 200) throw new Error('A nota pode ter até 200 caracteres.');
  return { transactionId: tx.id, sharedAmountCents: sendsPart(draft, tx) ? amount : null,
    note: draft.note.trim() || null, applyToSameDescription: draft.batch, alwaysSendBy,
    tagId: alwaysSendBy === 'tag' ? selectedRule!.tagId : null,
    windowStart: alwaysSendBy && space.kind === 'trip' ? space.startsAt : null,
    windowEnd: alwaysSendBy && space.kind === 'trip' && space.endsAt ? `${space.endsAt.slice(0, 10)}T23:59:59.999Z` : null };
}

export interface SentConfirmation { message: string; action: string; href: string }

const MONTH_NAME = new Intl.DateTimeFormat('pt-BR', { month: 'long', timeZone: 'UTC' });

/**
 * What the sender is told after a send, and where the link takes them. A space files an expense under the month of
 * the PURCHASE (the same UTC month bounds the Despesas page reads), never the month it was sent in: an August purchase
 * sent in September is invisible on the September page, for everyone. So the confirmation names that month and links
 * to it. A batch spans the months of its purchases, so it links to the selected one's. A trip has one period.
 */
export function sentConfirmation(space: Pick<SpaceDetail, 'id' | 'name' | 'kind'>, result: Pick<SendToSpaceResult, 'expense' | 'sentCount' | 'skippedCount' | 'rule'>): SentConfirmation {
  const suffix = `${result.rule ? ' Regra de envio salva.' : ''}${result.skippedCount ? ` ${result.skippedCount} lançamentos não precisaram ser enviados.` : ''}`;
  const sent = result.sentCount === 1 ? 'Despesa enviada' : `${result.sentCount} despesas enviadas`;
  const occurred = new Date(result.expense.occurredAt);
  if (space.kind === 'trip' || Number.isNaN(occurred.getTime())) {
    return { message: `${sent} para ${space.name}.${suffix}`, action: 'Ver despesas', href: spaceHref(space.id, 'expenses') };
  }
  const period = occurred.toISOString().slice(0, 7);
  const href = spaceHrefForPeriod(space.id, 'expenses', period);
  if (result.sentCount === 1) {
    return { message: `${sent} para ${space.name}. Ela aparece em ${settlementPeriodLabel(period)}, o mês da compra.${suffix}`, action: `Ver em ${MONTH_NAME.format(occurred)}`, href };
  }
  return { message: `${sent} para ${space.name}. Cada uma aparece no mês da sua compra.${suffix}`, action: 'Ver despesas', href };
}

export interface SentEventProperties { via: 'manual' | 'partial' | 'batch'; batch_count: number; rule_type: string | null; rule_created?: boolean }

/**
 * Properties of `space.expense.sent` (VER-421): how it was sent, how many rows entered, and the rule's TYPE (never
 * its value, which can be a description). `rule_created` is the API's own outcome: an API that does not report it
 * yet leaves the property OUT, because a cache cannot tell a created rule from a reused one.
 */
export function sentEventProperties(draft: Pick<SendDraft, 'mode' | 'amount' | 'batch'>, tx: Transaction,
  result: Pick<SendToSpaceResult, 'sentCount' | 'rule' | 'ruleCreated'>): SentEventProperties {
  const via: 'manual' | 'partial' | 'batch' = draft.batch ? 'batch' : sendsPart(draft, tx) ? 'partial' : 'manual';
  return { via, batch_count: result.sentCount, rule_type: result.rule?.type ?? null,
    ...(typeof result.ruleCreated === 'boolean' ? { rule_created: result.ruleCreated } : {}) };
}

// The personal page that opens the send drawer on one transaction, with the space already chosen. The provider reads
// these params once and drops them, so Back or a reload never reopens the drawer.
export const SEND_PARAM = 'send';
export const SEND_SPACE_PARAM = 'space';

export function sendHref(transactionId: string, spaceId: string): string {
  return `/transactions?${new URLSearchParams({ [SEND_PARAM]: transactionId, [SEND_SPACE_PARAM]: spaceId }).toString()}`;
}
